import { quoteApi } from "@/main/api";
import KOGCollapse from "@/main/components/shared/kogCollapse/kogCollapse";
import KogThreeDotsQuote from "@/main/components/shared/threeDotsQuote";
import { QuoteAwaitingApprovalResponse, QuoteDataResponse } from "@/main/models/response";
import BoxSupplierDisplay from "@/main/screens/order/viewOrder/components/awaitingApprovalCollapse/boxSupplier/boxSupplier";
import { Controls } from "@/main/services/constants/constantsControl";
import { getGuidScreenByRoute, getTranslateByKey, isShowControlByElementId } from "@/main/services/utils";
import { useEffect, useState } from "react";

import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import CopyToClipboardText from "@/main/components/shared/copyToClipboardText/copyToClipboardText";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import { TextEllipsis } from "@/main/components/shared/styles";
import TotalLossComponent from "@/main/components/shared/totalLossComponent/totalLossComponent";
import { ApproveOrRejectQuoteRequest } from "@/main/models/request";
import { RoutesConstants } from "@/main/routes/routesConstants";
import ApprovePartsModal, {
	IApprovalPartsForm,
} from "@/main/screens/order/viewOrder/components/awaitingApprovalCollapse/modals/approvePartsModal";
import RejectPartsModal, {
	IRejectPartsForm,
} from "@/main/screens/order/viewOrder/components/awaitingApprovalCollapse/modals/rejectPartsModal";
import { handleUpdateQuoteCountStatusType } from "@/main/screens/order/viewOrder/components/cardGrid/types";
import { ApprovalMatrixStatus } from "@/main/services/enum/ApprovalMatrixStatusEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";
import { onLoadDateRangePicker } from "@/main/services/utilsDate";
import { formatCurrency } from "@/main/services/utilsNumber";
import { userIsSupplier } from "@/main/services/utilsUser";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as S from "./styles";
interface IAwaitingApprovalCollapse {
	data: QuoteDataResponse;
	handleUpdateQuoteCountStatus: handleUpdateQuoteCountStatusType;
}

const sortParts = (items: any[]) => {
	if (!items) return [];
	return items.sort((a, b) => {
		const infoA = a.quoteItem?.quoteProcurementPartInfo || {};
		const infoB = b.quoteItem?.quoteProcurementPartInfo || {};

		const nameA = infoA.partName || infoA.quoteItemDescription || "";
		const nameB = infoB.partName || infoB.quoteItemDescription || "";

		return nameA.localeCompare(nameB);
	});
};

const sortSuppliersAndParts = (suppliers: any[]) => {
	if (!suppliers) return [];

	const sortedSuppliers = suppliers.sort((a, b) => {
		const nameA = a.companyName || "";
		const nameB = b.companyName || "";
		return nameA.localeCompare(nameB);
	});

	return sortedSuppliers.map((supplier) => ({
		...supplier,
		quoteSupplierItems: sortParts(supplier.quoteSupplierItems),
	}));
};

export default function AwaitingApprovalCollapse({ data, handleUpdateQuoteCountStatus }: IAwaitingApprovalCollapse) {
	const [dataQuote, setDataQuote] = useState<QuoteAwaitingApprovalResponse>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isOpenApproveModal, setIsOpenApproveModal] = useState<boolean>(false);
	const [isOpenRejectModal, setIsOpenRejectModal] = useState<boolean>(false);

	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const showTotalLoss = !userIsSupplier();

	const navigate = useNavigate();
	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchOrderDaysInterval);

	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

	useEffect(() => {
		load();
	}, []);

	function load() {
		setIsLoading(true);
		quoteApi
			.getDetailsAwaitingApproval(data.guidQuote)
			.then((res) => {
				const { result } = res.data;
				if (result) {
					result.pendingApproval = sortSuppliersAndParts(result.pendingApproval);
					result.assigned = sortSuppliersAndParts(result.assigned);
					result.rejected = sortSuppliersAndParts(result.rejected);

					setDataQuote(result);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	}

	function updateSearch() {
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		const urlRefresh = `${RoutesConstants.orderSearchOrder}?quoteFilterVehicle=${data.claimNumber}&startDate=${firstDateResult}&finalDate=${secondDateResult}`;
		navigate(urlRefresh);
		handleUpdateQuoteCountStatus();
	}

	async function handleApproveSubmit(formData: IApprovalPartsForm) {
		const message = getTranslateByKey("information.confirmApproveParts");
		const confirmed = await getConfirmation(message);

		if (confirmed) {
			const request: ApproveOrRejectQuoteRequest = {
				idApprovalMatrixStatus: ApprovalMatrixStatus.Approved,
				guidQuoteSupplierItems: formData.quoteSupplierItemGuids,
			};
			return quoteApi.approveOrRejectParts(request).then((res) => {
				toast.success(getTranslateByKey("information.partsApprovedSuccess"));
				setIsOpenApproveModal(false);
				if (res.data.result) {
					updateSearch();
					return;
				}
				load();
			});
		}
	}

	async function handleRejectSubmit(formData: IRejectPartsForm) {
		const message = getTranslateByKey("information.confirmRejectParts");
		const confirmed = await getConfirmation(message);

		if (confirmed) {
			const request: ApproveOrRejectQuoteRequest = {
				idApprovalMatrixStatus: ApprovalMatrixStatus.Rejected,
				guidQuoteSupplierItems: formData.quoteSupplierItemGuids,
				reason: formData.reasonDescription ?? undefined,
				idReason: formData.reason.enum,
			};
			return quoteApi.approveOrRejectParts(request).then((res) => {
				toast.success(getTranslateByKey("information.partsRejectedSuccess"));
				setIsOpenRejectModal(false);
				if (res.data.result) {
					updateSearch();
					return;
				}
				load();
			});
		}
	}

	if (!dataQuote || isLoading) {
		return <LoadingComponent />;
	}

	const totalPendingApproval =
		dataQuote.pendingApproval?.reduce((acc, supplier) => acc + (supplier.totalPrice || 0), 0) || 0;
	const totalAssigned = dataQuote.assigned?.reduce((acc, supplier) => acc + (supplier.totalPrice || 0), 0) || 0;

	const totalPrice = totalPendingApproval + totalAssigned;
	const isQuoteCanceled = data.idStatus === ProcurementStatusEnum.Cancelled;

	return (
		<S.Container>
			<S.AreaHeader>
				<S.AreaHeaderLeft>
					<ColumnLabel
						label={getTranslateByKey("information.vin")}
						content={
							<CopyToClipboardText
								maxWidth="190px"
								label={getTranslateByKey("information.vin")}
								valueToCopy={dataQuote.vin}
							>
								<TextEllipsis>{dataQuote.vin}</TextEllipsis>
							</CopyToClipboardText>
						}
					/>
					{showTotalLoss && <TotalLossComponent type="column" idStatus={data.idStatus} guidQuote={data.guidQuote} />}
				</S.AreaHeaderLeft>
				<S.AreaHeaderRight>
					<KogThreeDotsQuote.Root>
						<KogThreeDotsQuote.OptionHistory
							guidQuote={data.guidQuote}
							guidScreen={getGuidScreenByRoute(RoutesConstants.orderSearchOrder)}
						/>

						<KogThreeDotsQuote.OptionImage
							guidQuote={data.guidQuote}
							guidClaim={data.guidClaim}
							isQuoteCanceled={isQuoteCanceled}
							canDownloadImages={isShowControlByElementId(Controls.IdDownloadImagesSearchOrdersControl)}
						/>
					</KogThreeDotsQuote.Root>
				</S.AreaHeaderRight>
			</S.AreaHeader>
			<S.Title>{getTranslateByKey("information.partsPendingApproval")}</S.Title>
			<S.AreaCollapses>
				{dataQuote.pendingApproval && dataQuote.pendingApproval.length > 0 && (
					<>
						{dataQuote.pendingApproval.map((supplier) => (
							<BoxSupplierDisplay key={supplier.guidQuoteSupplier} data={supplier} showPrices={showPrices} />
						))}
					</>
				)}

				{dataQuote.assigned && dataQuote.assigned.length > 0 && (
					<KOGCollapse
						isOpenCollapseinitial={false}
						changeBorderOnExpand={false}
						header={
							<S.CollapseHeader>
								<S.CollapseHeaderText>{getTranslateByKey("procurementStatus.assigned")}</S.CollapseHeaderText>
							</S.CollapseHeader>
						}
						content={
							<>
								{dataQuote.assigned.map((supplier) => (
									<BoxSupplierDisplay key={supplier.guidQuoteSupplier} data={supplier} showPrices={showPrices} />
								))}
							</>
						}
					/>
				)}

				{dataQuote.rejected && dataQuote.rejected.length > 0 && (
					<KOGCollapse
						isOpenCollapseinitial={false}
						changeBorderOnExpand={false}
						header={
							<S.CollapseHeader>
								<S.CollapseHeaderText>{getTranslateByKey("information.rejected")}</S.CollapseHeaderText>
							</S.CollapseHeader>
						}
						content={
							<>
								{dataQuote.rejected.map((supplier) => (
									<BoxSupplierDisplay
										key={supplier.guidQuoteSupplier}
										data={supplier}
										showPrices={showPrices}
										isRejected
									/>
								))}
							</>
						}
					/>
				)}
			</S.AreaCollapses>

			{showPrices && (
				<S.AreaPrice>
					<h6 style={{ fontWeight: "bold" }}>{getTranslateByKey("information.total")}</h6>
					<h6 style={{ fontWeight: "bold" }}>{formatCurrency(totalPrice)}</h6>
				</S.AreaPrice>
			)}

			<S.AreaButtons>
				{dataQuote.showApprovalButton && (
					<SecondaryButton onClick={() => setIsOpenRejectModal(true)}>
						{getTranslateByKey("information.reject")}
					</SecondaryButton>
				)}
				{dataQuote.showApprovalButton && (
					<Button onClick={() => setIsOpenApproveModal(true)}>{getTranslateByKey("information.approve")}</Button>
				)}
			</S.AreaButtons>

			<ApprovePartsModal
				isOpen={isOpenApproveModal}
				onClose={() => setIsOpenApproveModal(false)}
				data={dataQuote.pendingApproval}
				showPrices={showPrices}
				onSubmitAction={handleApproveSubmit}
			/>

			<RejectPartsModal
				isOpen={isOpenRejectModal}
				onClose={() => setIsOpenRejectModal(false)}
				data={dataQuote.pendingApproval}
				showPrices={showPrices}
				onSubmitAction={handleRejectSubmit}
			/>

			<ModalConfirmation />
		</S.Container>
	);
}
